import { ChangeDetectorRef, Component, computed, input, OnInit, signal } from '@angular/core';
import { IncidentService } from '../../shared/incident.service';
import { Document, Incident, INCIDENT_TABLE_CLOSED_COLUMNS, IncidentTableColumn } from '../../models/incident';
import { AuthService } from '../../auth.service';
import { Globals } from '../../shared/functions';
import { Router } from '@angular/router';
import { User as AuthUser } from '../../models/user';
import { NotificationService } from '../../shared/notification.service';
import { Base64Report } from '../../models/report';
import { ReportService } from '../../shared/report.service';
import { QuoteService } from '../../shared/quote.service';
import { IncidentSearch } from "../incident-search/incident-search";
import { AppModule } from "../../app.module";
import { FileUploader } from "../../shared/file-uploader/file-uploader";
import { MapTrackSpTech } from "../../map/map-track-sp-tech/map-track-sp-tech";
import { IncidentCloseModal } from "../incident-close-modal/incident-close-modal";
import { ClientLink, IncidentClientLink } from '../../models/client';

@Component({
  selector: 'app-closed-incident-table',
  imports: [IncidentSearch, AppModule, FileUploader, MapTrackSpTech, IncidentCloseModal],
  templateUrl: './closed-incident-table.html',
  styleUrl: './closed-incident-table.less'
})
export class ClosedIncidentTable implements OnInit {

  currentUser = signal<AuthUser | null>(null);
  public incidents = computed(() => this.incidentService.closedIncidents());
  public isLoading = computed(() => this.incidentService.isLoadingClosed$());

  etaChanges = input<Map<number, number>>(new Map());
  isTrackTechModalVisible = false;
  public selectedIncidentId = signal<number>(0);
  public selectedUploadDocumentClassificationId = signal<number>(1);

  public isUploadModalVisible = false;
  public isCloseIncidentModalVisible = false;
  public selectedIncidentIdToClose: number = 0;;

  public columns = computed<IncidentTableColumn[]>(() => {
    const user = this.authService.user;
    const userClientTypes = user?.clientTypes || [];

    return INCIDENT_TABLE_CLOSED_COLUMNS
      .filter((config) =>
        userClientTypes.some((clientType) =>
          config.visibleFor.includes(clientType)
        )
      )
      .map((config) => ({
        key: config.key,
        title: config.title,
        width: config.width,
      }));
  });

  public statusCounts = computed(() => {
    const counts = new Map<string, number>();
    this.incidents().forEach(incident => {
      const status = incident.status || 'Unknown';
      counts.set(status, (counts.get(status) || 0) + 1);
    });
    return Array.from(counts.entries()).map(([status, count]) => ({ status, count }));
  });

  public gFuncs = Globals.Functions;

  constructor(
    private incidentService: IncidentService,
    public authService: AuthService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private notification: NotificationService,
    private reportService: ReportService,
    private quoteService: QuoteService
  ) {
    setInterval(() => {
      this.cdr.markForCheck();
    }, 60000);
    }

  public searchTerm = signal<string>('');
  public dateRange = signal<[Date, Date] | null>([new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), new Date()]);
  

public filteredIncidents = computed(() => {
  let filtered = this.incidents();
  const searchTerm = this.searchTerm().toLowerCase();  
  if (searchTerm && searchTerm.trim() !== '') {
    filtered = filtered.filter(incident => {
      const locationMatch = incident.location?.description?.toLowerCase().includes(searchTerm) || false;
      const driverMatch = incident.driver?.name?.toLowerCase().includes(searchTerm) || false;
      const vehicleMatch = incident.fleetVehicle?.registrationNumber?.toLowerCase().includes(searchTerm) || false;
      const statusMatch = incident.status?.toLowerCase().includes(searchTerm) || false;
      const incidentNumberMatch = incident.incidentNumber?.toLowerCase().includes(searchTerm) || false;
      const clientNameMatch = incident.clientName?.toLowerCase().includes(searchTerm) || false;
      const orderNumberMatch = incident.orderNumber?.toLowerCase().includes(searchTerm) || false;
      
      return locationMatch || driverMatch || vehicleMatch || statusMatch || incidentNumberMatch || clientNameMatch || orderNumberMatch;
    });
  }
  return filtered;
});

  ngOnInit() {
    this.currentUser.set(this.authService.user || null);
    this.onDateRangeChange(this.dateRange());
  }

  public onClickViewIncident(incident: Incident) {
      this.router.navigate(['/incident', incident.incidentId]);
    }
isLoadingProofOfWork: boolean = false;

  public onClickProofOfWork(incidentId: number) {
    this.isLoadingProofOfWork = true;
    this.reportService.generateProofOfWorkReport(incidentId).subscribe({
      next: (report: Base64Report) => {
        this.notification.showSuccess(
          'Proof of Work report generated successfully.'
        );
        this.downloadFile(report);
        this.isLoadingProofOfWork = false;
      },
      error: () => {
        this.notification.showError('Failed to generate Proof of Work report.');
        this.isLoadingProofOfWork = false;
      },
    });
  }

  private downloadFile(report: Base64Report) {
    const link = document.createElement('a');
    link.href = 'data:' + report.fileType + ';base64,' + report.base64Data;
    link.download = report.fileName;
    link.click();
  }

  public getActiveQuotes(incidentClients: any[]): any[] {
    return incidentClients.filter((ic) => ic.activeQuoteId !== null);
  }

  public getServiceProviderName(incident: Incident): string {
    const spLink = incident.incidentClients.find(
      (link) => link.connectionTypeId === 3 && link.isActive
    );
    return spLink ? spLink.clientName : '';
  }

  public getServiceProviderDriverName(incident: Incident): string {
    const spLink = incident.incidentOperators.find(
      (link) => link.userType === 5 && link.isActive
    );
    return spLink ? spLink.name : '';
  }

  public getFleetName(incident: Incident): string {
    return incident && incident.clientName ? incident.clientName : 'Unknown';
  }

  public getCallCentreName(incident: Incident): string {
    const cc = incident.incidentOperators.find((io) => io.isActive === null); //Need to confirm condition
    return cc ? cc.name : 'Unknown';
  }

  // public calculateEstimatedArrival(incident: Incident): string {
  //   const dtLogged = incident.dtLogged;
  //   const sp = incident.incidentClients.find(ic => ic.isActive === true && ic.incidentTravelTimeMinutes > 0); //confirm how to identify service provider with status/isActive/quote etc
  //   const incidentTravelTimeMinutes = sp ? sp.incidentTravelTimeMinutes : 0;

  //   if (!dtLogged || !incidentTravelTimeMinutes) return 'N/A';

  //   const loggedDate = new Date(dtLogged + 'Z');
  //   const arrivalTime = new Date(loggedDate.getTime()  + incidentTravelTimeMinutes * 60000);
  //   return arrivalTime.toLocaleTimeString() + ' (' + incidentTravelTimeMinutes + ' mins)';
  // }

  public getIncidentStatusPercentage(statusId: number): number {
    return (statusId / 11) * 100;
  }

  public formatStatus(incident: Incident): string {
    const days = this.getIncidentStatusPercentage(incident.statusId);
    return `${days} Days`;
  }

  public viewDoc(incident: Incident, docTypeId: number): void {
    const docUrl = incident.documents.find(
      (doc) => doc.documentClassificationId === docTypeId
    )?.url;
    if (docUrl) {
      window.open(docUrl, '_blank');
    } else {
      this.notification.showError('Document not available.');
    }
  }

  public isTechLate(incident: Incident): boolean {
    if (incident.statusId === 7) { // 7 = Technician en route
    return incident.originalEta < incident.breakdownTechnicianDriverEta;
  }
  else{
    return false;
  }
}

  public calculateTimeElapsed(dtLogged: Date): string {
    const loggedTime = new Date(dtLogged + 'Z').getTime();
    const currentTime = new Date().getTime();
    const elapsedMinutes = (currentTime - loggedTime) / (1000 * 60);
    return `${Math.floor(elapsedMinutes)} mins`;
  }

  public calculateEstimatedArrival(incident: Incident): string {
    if (incident.breakdownTechnicianDriverEta) {
      const eta = new Date(incident.breakdownTechnicianDriverEta + 'Z');
      const travelTime = this.gFuncs.calculateMinutes(
        incident.breakdownTechnicianDriverEta
      );

      return `${eta.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      })} (${travelTime} mins)`;
    }
    return 'Not Assigned';
  }

  public getTravelTime(incident: Incident): number | null {
    if (incident.breakdownTechnicianDriverEta) {
      return this.gFuncs.calculateMinutes(
        incident.breakdownTechnicianDriverEta
      );
    }
    return null;
  }

  public isBDOverTime(incident: Incident): boolean {
    return false;
  }

  public onClickUploadPoW(incidentId: number) {
    this.selectedIncidentId.set(incidentId);
    this.selectedUploadDocumentClassificationId.set(1); // Proof of Work
    this.isUploadModalVisible = true;
  }

  public onClickUploadSPInvoice(incidentId: number) {
    this.selectedIncidentId.set(incidentId);
    this.selectedUploadDocumentClassificationId.set(4); // Service Provider Invoice
    this.isUploadModalVisible = true;
  }

  public onClickUploadFleetInvoice(incidentId: number) {
    this.selectedIncidentId.set(incidentId);
    this.selectedUploadDocumentClassificationId.set(6); // Fleet Invoice
    this.isUploadModalVisible = true;
  }

  public hasSPInvoice(incident: Incident): boolean {
    //Property in Incident class is not updating, need to convert to signal to work
    return incident.documents.some(
      (doc) => doc.documentClassificationId === 4
    ); // 4 = Invoice external
  }

  public hasFleetInvoice(incident: Incident): boolean {
    return incident.documents.some(
      (doc) => doc.documentClassificationId === 6
    ); // 6 = Fleet Invoice
  }

 
  public onUploadComplete(event: Document[]): void {
    // Add documents to incident, with duplication checking
    const incident = this.incidents().find(
      (inc) => inc.incidentId === this.selectedIncidentId()
    );
    if (incident) {
      event.forEach((newDoc) => {
        const exists = incident.documents.some(
          (doc: Document) => doc.documentId === newDoc.documentId
        );
        if (!exists) {
          incident.documents.push(newDoc);
          // Update SP Invoce status to 12 when SP invoice is uploaded
          if (newDoc.documentClassificationId === 4) {
            incident.statusId = 12;
            this.notification.showSuccess('Service Provider invoice uploaded.');
          }
          // Update incident status to 17 when fleet invoice is uploaded
          if (newDoc.documentClassificationId === 6) {
            incident.statusId = 17;
            this.notification.showSuccess('Fleet invoice uploaded.');
          }
        }
      });
    }
    this.isUploadModalVisible = false;
  }

  public onClickViewSPInvoice(incidentId: number) {
    const incident = this.incidents().find(
      (inc) => inc.incidentId === incidentId
    );
    if (incident) {
      const invoiceDoc = incident.documents.find(
        (doc: Document) => doc.documentClassificationId === 4
      );
      if (invoiceDoc && invoiceDoc.url) {
        window.open(invoiceDoc.url, '_blank');
      } else {
        this.notification.showError('Service Provider Invoice not available.');
      }
    }
  }

  public onClickViewFleetInvoice(incidentId: number) {
    const incident = this.incidents().find(
      (inc) => inc.incidentId === incidentId
    );
    if (incident) {
      const invoiceDoc = incident.documents.find(
        (doc: Document) => doc.documentClassificationId === 6
      );
      if (invoiceDoc && invoiceDoc.url) {
        window.open(invoiceDoc.url, '_blank');
      } else {
        this.notification.showError('Fleet Invoice not available.');
      }
    }
  }

  public onClickGoToServiceProviderQuote(incidentId: number) {
    const quoteId = this.incidents().find(inc => inc.incidentId === incidentId)?.incidentClients.find((link: IncidentClientLink) => link.connectionTypeId === 3 && link.isActive === true)?.activeQuoteId;
    if (quoteId && quoteId > 0)
      this.router.navigate(['/quote', quoteId]);
    else
      this.notification.showWarning("No active quote found for the selected service provider.");
  }

  public getServiceProviderQuoteTotal(incidentId: number): number {
    let quoteSummary = this.incidents().find(inc => inc.incidentId === incidentId)?.incidentClients.find((link: IncidentClientLink) => link.connectionTypeId === 3 && link.isActive === true)?.quoteSummary;
    if (quoteSummary && quoteSummary.quoteOptions.length > 0) {
      return quoteSummary.quoteOptions[0].total;
    }
    else {
      return 0;
    }
  }

  public getFleetQuoteTotal(incidentId: number): number {
    let quoteSummary = this.incidents().find(inc => inc.incidentId === incidentId)?.incidentClients.find((link: IncidentClientLink) => link.connectionTypeId === 2 && link.isActive === true)?.quoteSummary;
    if (quoteSummary && quoteSummary.quoteOptions.length > 0) {
      return quoteSummary.quoteOptions[0].total;
    }
    else {
      return 0;
    }
  }

  public onClickReInvoiceCustomer(incidentId: number) {
    const quoteId = this.incidents().find(inc => inc.incidentId === incidentId)?.incidentClients.find((link: IncidentClientLink) => link.connectionTypeId === 3 && link.isActive === true)?.activeQuoteId;
    const incident = this.incidents().find(inc => inc.incidentId === incidentId);
    if (quoteId && quoteId > 0 && incident) {
      this.quoteService.generateQuoteToFleet(quoteId, incidentId, incident?.clientId, this.authService.user!.clientId).subscribe({
        next: (response) => {
          this.notification.showSuccess('Quote generated for fleet');
          incident.statusId = 16; //set incident status to fleet invoiced
          this.router.navigate(['/quote', response.quoteId]);
        },
        error: (error) => {
          this.notification.handleError(error, "Failed to generate quote for fleet.");
        }
      });
    }
  }

  public onClickCompleteIncident(incidentId: number) {
    this.selectedIncidentIdToClose = incidentId;
    this.isCloseIncidentModalVisible = true;
  }

  public onIncidentClosed() {
    // Refresh the incident in the list
    const incident = this.incidents().find(
      (inc) => inc.incidentId === this.selectedIncidentIdToClose
    );
    if (incident) {
      // Optionally update the status or trigger a refresh from parent
      this.notification.showSuccess('Breakdown closed successfully.');
    }
    this.isCloseIncidentModalVisible = false;
    this.cdr.markForCheck();
  }

  /**
   * Gets the background color for action columns based on incident status
   * @param columnKey - The key of the column (pow, spinvoice, updatepo, reinvoice, invoice)
   * @param incident - The incident data
   * @returns Background color: '#d4edda' (green) when done, '#f2d2aa' (orange) when current action, '' (none) when not applicable
   * This will need to be incorporated into Nic's existing column layout logic in the incident class
   */
  public getActionColumnBackground(columnKey: string, incident: Incident): string {
    const statusId = incident.statusId;
    
    switch (columnKey) {
      case 'pow':
        if (statusId >= 11) return '#d4edda'; // green
        if (statusId >= 5 && statusId < 11) return '#f2d2aa'; // orange
        return ''; // no color
      
      case 'spinvoice':
        if (statusId >= 12) return '#d4edda'; // green
        if (statusId >= 11 && statusId < 12 && 24 < this.calculateHoursSince(incident.incidentActions?.dtWorkReportAssigned) && this.calculateHoursSince(incident.incidentActions?.dtWorkReportAssigned) <=  48) return '#f2d2aa'; // orange
        if (statusId >= 11 && statusId < 12 && this.calculateHoursSince(incident.incidentActions?.dtWorkReportAssigned) >  48) return '#f2aaaa'; // red
        return ''; // no color
      
      case 'updatepo':
        if (statusId > 12) return '#d4edda'; // green
        if (statusId === 12 && 24 < this.calculateHoursSince(incident.incidentActions?.dtSPInvoiceUploaded) && this.calculateHoursSince(incident.incidentActions?.dtSPInvoiceUploaded) <=  48) return '#f2d2aa'; // orange
        if (statusId === 12 && this.calculateHoursSince(incident.incidentActions?.dtSPInvoiceUploaded) >  48) return '#f2aaaa'; // red
        return ''; // no color
      
      case 'reinvoice':
        if (statusId > 15) return '#d4edda'; // green
        if (statusId === 15 && 24 < this.calculateHoursSince(incident.incidentActions?.dtPOUpdated) && this.calculateHoursSince(incident.incidentActions?.dtPOUpdated) <=  48) return '#f2d2aa'; // orange
          if (statusId === 15 && this.calculateHoursSince(incident.incidentActions?.dtPOUpdated) >  48) return '#f2aaaa'; // red
        return ''; // no color
      
      case 'invoice':
        if (statusId > 16) return '#d4edda'; // green
        if (statusId === 16 && 24 < this.calculateHoursSince(incident.incidentActions?.dtReInvoiced) && this.calculateHoursSince(incident.incidentActions?.dtReInvoiced) <=  48) return '#f2d2aa'; // orange
        if (statusId === 16 && this.calculateHoursSince(incident.incidentActions?.dtReInvoiced) >  48) return '#f2aaaa'; // red
        return ''; // no color
      
      default:
        return ''; // no color for other columns
    }
  }

  public trackTechForIncident = signal<Incident | null>(null);

  public openTrackDriverModal(incident: Incident): void {
    this.trackTechForIncident.set(incident);
    this.isTrackTechModalVisible = true;
  }

    public onSearchTermChange(searchTerm: string) {
    this.searchTerm.set(searchTerm);
  }

  public onDateRangeChange(dateRange: [Date, Date] | null) {
    this.dateRange.set(dateRange);
    if (dateRange && dateRange[0] && dateRange[1]) {
      // Load incidents for the selected date range
      this.incidentService.loadClosedIncidentsForDateRange(dateRange[0], dateRange[1]).subscribe({
        next: (incidents) => {
          this.notification.showSuccess(`Loaded breakdowns from ${dateRange[0].toLocaleDateString()} to ${dateRange[1].toLocaleDateString()}`);
        },
        error: (error) => {
          this.notification.showError('Failed to load breakdowns for selected date range', error.message);
        }
      });
    } else {
      // Load today's incidents when date range is cleared
      this.incidentService.loadClosedIncidents().subscribe({
        next: (incidents) => {
          this.notification.showSuccess(`Loaded breakdowns succesfully.`);
        },
        error: (error) => {
          this.notification.showError('Failed to load breakdowns', error.message);
        }
      });
    }
  }

  public onClearFilters() {
    this.searchTerm.set('');
    this.dateRange.set(null);
    
    this.incidentService.loadClosedIncidents().subscribe({
      next: (incidents) => {
        this.notification.showSuccess(`Loaded breakdowns succesfully.`);
      },
      error: (error) => {
        this.notification.showError('Failed to load breakdowns', error.message);
      }
    });
  }

  private calculateHoursSince(dt: Date | undefined | null): number {
    if (!dt) return 0;
    const startDt = new Date(dt + 'Z').getTime();
    const currentTime = new Date().getTime();
    const elapsedMinutes = (currentTime - startDt) / (1000 * 60);
    return Math.floor(elapsedMinutes / 60);
  }


  public dtWorkReportAssigned(incident: Incident): string {
    return incident.incidentActions?.dtWorkReportAssigned ? new Date(incident.incidentActions.dtWorkReportAssigned + 'Z').toLocaleString() : '';
  }

  public dtSPInvoiceUploaded(incident: Incident): string {
    return incident.incidentActions?.dtSPInvoiceUploaded ? new Date(incident.incidentActions.dtSPInvoiceUploaded + 'Z').toLocaleString() : '';
  }

  public dtPOUpdated(incident: Incident): string {
    return incident.incidentActions?.dtPOUpdated ? new Date(incident.incidentActions.dtPOUpdated + 'Z').toLocaleString() : '';
  }

  public dtReInvoiced(incident: Incident): string {
    return incident.incidentActions?.dtReInvoiced ? new Date(incident.incidentActions.dtReInvoiced + 'Z').toLocaleString() : '';
  }

  public dtInvoiceUploaded(incident: Incident): string {
    return incident.incidentActions?.dtInvoiceUploaded ? new Date(incident.incidentActions.dtInvoiceUploaded + 'Z').toLocaleString() : '';
  }

  public dtClosed(incident: Incident): string {
    return incident.incidentActions?.dtClosed ? new Date(incident.incidentActions.dtClosed + 'Z').toLocaleString() : '';
  }
}
